import type { components } from "@scalty/contracts";

type DatasetKind = components["schemas"]["Dataset"]["kind"];
type ModelType = components["schemas"]["TrainRequest"]["modelType"];

/**
 * The bounds one hyperparameter is validated against, as the base-model catalog
 * publishes them (`BaseModel.hyperparamBounds`). The bundle carries NO copy of
 * these numbers: a client-side copy is what broke GT-01 rehearsal 2 — the API
 * had resolved 20 epochs while the browser, still running the previous bundle,
 * refused anything above 10 and left "Start training" dead on the product's own
 * defaults.
 */
export type HyperparamBound = components["schemas"]["HyperparamBound"];
export type HyperparamBounds = components["schemas"]["HyperparamBounds"];

export type TrainHyperparams = {
  rank: number | null;
  alpha: number | null;
  learningRate: number | null;
  epochs: number | null;
  maxSeqLen: number | null;
};

export type TrainHyperparam = keyof TrainHyperparams;
export type TrainHyperparamErrors = Partial<Record<TrainHyperparam, string>>;

/** Field labels, in the words the inputs are labelled with. */
const HYPERPARAM_LABELS: Record<TrainHyperparam, string> = {
  rank: "Rank",
  alpha: "Alpha",
  learningRate: "Learning rate",
  epochs: "Epochs",
  maxSeqLen: "Sequence length",
};

/**
 * The model type a dataset kind settles on its own, or undefined when the form must ask.
 *
 * The API decides this, not the portal: `versionCanTrainText` in the models service accepts a
 * text run on a conversations or a documents dataset and refuses one on tabular data
 * (`ErrDatasetNotChat`). Tabular datasets train either tabular task, and the API accepts both,
 * so the kind alone settles nothing and the form asks which — returning one of them here would
 * withhold the other.
 */
export function modelTypeForDatasetKind(kind: DatasetKind | null | undefined): ModelType | undefined {
  return kind === "conversations" || kind === "documents" ? "text_generation" : undefined;
}

/** The `step` a number input advances by: whole numbers step by one, and the
 *  learning rate by the smallest increment worth typing with an arrow key. It
 *  is presentation, not a bound — nothing is rejected for missing a step. */
export function hyperparamStep(bound: HyperparamBound): number {
  return bound.integer ? 1 : 0.00005;
}

/** The bound as the label's range hint, or null when the API enforces neither
 *  end — the label then says nothing rather than implying a limit. */
export function boundRangeLabel(bound: HyperparamBound): string | null {
  if (bound.min !== null && bound.max !== null) return `${bound.min}–${bound.max}`;
  if (bound.max !== null) return `up to ${bound.max}`;
  if (bound.min !== null && bound.min > 0) return `from ${bound.min}`;
  return null;
}

/** The reason a value is refused, in the same words the bound is stated in, or
 *  undefined when the bound accepts it. */
export function boundError(
  label: string,
  bound: HyperparamBound,
  value: number,
): string | undefined {
  if (bound.integer && !Number.isInteger(value)) {
    return boundMessage(label, bound);
  }
  if (!Number.isFinite(value)) return boundMessage(label, bound);
  if (bound.min !== null && value < bound.min) return boundMessage(label, bound);
  if (bound.max !== null && value > bound.max) return boundMessage(label, bound);
  if (bound.exclusiveMin !== null && value <= bound.exclusiveMin) {
    return boundMessage(label, bound);
  }
  return undefined;
}

function boundMessage(label: string, bound: HyperparamBound): string {
  const kind = bound.integer ? "a whole number" : "a number";
  if (bound.min !== null && bound.max !== null) {
    return `${label} must be ${kind} from ${bound.min} to ${bound.max}.`;
  }
  if (bound.max !== null) return `${label} must be ${kind} up to ${bound.max}.`;
  if (bound.exclusiveMin !== null) {
    return `${label} must be ${kind} greater than ${bound.exclusiveMin}.`;
  }
  if (bound.min !== null) return `${label} must be ${kind} of at least ${bound.min}.`;
  return `${label} must be ${kind}.`;
}

/**
 * The fields whose value the catalog's bounds refuse. `bounds` is null until the
 * catalog answers: the form then adds no rule of its own — the server still
 * adjudicates every value at train time, and inventing a bound here is exactly
 * the defect this replaced.
 */
export function trainHyperparamErrors(
  values: TrainHyperparams,
  bounds: HyperparamBounds | null | undefined,
): TrainHyperparamErrors {
  const errors: TrainHyperparamErrors = {};
  if (bounds === null || bounds === undefined) return errors;
  for (const knob of Object.keys(HYPERPARAM_LABELS) as TrainHyperparam[]) {
    const value = values[knob];
    if (value === null) continue;
    const message = boundError(HYPERPARAM_LABELS[knob], bounds[knob], value);
    if (message !== undefined) errors[knob] = message;
  }
  return errors;
}
